import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  useMatch,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import theme from "../../themes";
import { Empty, Input, Select, Tooltip, Dropdown, App, Checkbox } from "antd";
import type { MenuProps } from "antd";
import {
  DownOutlined,
  LeftOutlined,
  LoadingOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../store";
import { getWhatsappStatsThunk } from "../../store/actions/whatsapp.action";
import { IMailConversation, IMailMessage } from "../../models/mail.model";
import { ICall } from "../../models/call.model";
import { IWhatsappConversation, IWhatsappMessage } from "../../models/whatsapp.model";
import { ROLES } from "../../models/user.model";
import { getCalls } from "../../services/call.service";
import {
  assignWhatsappConversation,
  getInboxConversations,
  getWhatsappConversationById,
} from "../../services/whatsapp.service";
import { getAgents } from "../../services/agent.service";
import { getUsers } from "../../services/user.service";
import { getCampaignsThunk } from "../../store/actions/campaign.action";
import { getLinesThunk } from "../../store/actions/line.action";
import { ILineResponse } from "../../models/line.model";
import { IAgent } from "../../models/agent.model";
import ErrorBoundary from "../../components/ErrorBoundary";
import ContentLoading, { filterControlCss } from "../../components/ContentLoading";
import WhatsappDetail from "./WhatsappDetail";
import { useChannelSettings } from "../../hooks/useChannelSettings";
import useThreadPolling from "../../hooks/useThreadPolling";
import ToastMessage from "../../utils/toastMessage";
import {
  TICKET_DISPLAY_STATUS,
  TICKET_STATE,
  HANDLED_BY_FILTER,
  asStatusToken,
  displayStatusOf as ticketDisplayStatusOf,
  handledByFromAiOnly,
  isClosedTicket,
  isFailedTicket,
  isNeedsAssign,
  matchesHandledByFilter,
  parseHandledByFilter,
  parseInboxStatus,
  type AssignedToValue,
  type HandledByFilter,
} from "../../utils/ticketStatus";

const { Option, OptGroup } = Select;

type TeamMember = {
  _id: string;
  full_name?: string;
  fullName?: string;
  email?: string;
  deleted_at?: string | Date | null;
};

type InboxChannel = "call" | "sms" | "whatsapp" | "email";
type ChannelFilter = "all" | InboxChannel;

type InboxRow = {
  id: string;
  channel: InboxChannel;
  phone: string;
  name: string;
  snippet: string;
  when: string;
  sortAt: number;
  status: string;
  internal_status?: string;
  handling_mode?: string;
  awaiting?: string;
  state?: string;
  assigned_to?: AssignedToValue;
  escalated_at?: string | null;
  resolution?: string | null;
  summary?: string;
  ticketId?: string | null;
  campaignId?: string | null;
  campaignName?: string;
  call?: ICall;
  mail?: IMailConversation;
  sms?: IWhatsappConversation;
  channels?: InboxChannel[];
  /** AI agent id fallback when `assigned_to` is missing (e.g. call rows). */
  agentId?: string | null;
  agentName?: string | null;
};

type AssigneeDisplay = {
  label: string;
  bg: string;
  fg: string;
};

const FILTER_ALL = "all";
const ASSIGNEE_AGENT_PREFIX = "agent:";
const ASSIGNEE_USER_PREFIX = "user:";

const teamMemberLabel = (user: TeamMember) =>
  String(user.full_name || user.fullName || user.email || "").trim() ||
  String(user._id);

const CHANNEL_FILTERS: ChannelFilter[] = [
  "all",
  "call",
  "sms",
  "whatsapp",
  "email",
];

const CHANNEL_ORDER: InboxChannel[] = ["call", "sms", "whatsapp", "email"];
const MESSAGING_INBOX_CHANNELS: Exclude<InboxChannel, "call">[] = [
  "whatsapp",
  "sms",
  "email",
];
const INBOX_API_CHANNEL: Record<Exclude<InboxChannel, "call">, string> = {
  sms: "SMS",
  whatsapp: "WHATSAPP_MESSAGE",
  email: "EMAIL",
};

const PAGE_SIZE = 10;
// List polls slower than an open thread (5s): each tick can hit 4 channel
// endpoints. 15s is snappy enough for new inbound without a loading flash.
const INBOX_POLL_MS = 15000;

const needsReplyApiChannel = (
  requested: ChannelFilter,
  isEnabled: (key: string) => boolean
): string | null => {
  const enabled = MESSAGING_INBOX_CHANNELS.filter(
    (channel) =>
      isEnabled(channel) && (requested === "all" || requested === channel)
  );
  if (!enabled.length) return null;
  if (enabled.length === 1) return INBOX_API_CHANNEL[enabled[0]];
  return "ALL";
};

const asNeedsReplyCount = (value: unknown): number | null => {
  const count = Number(value);
  if (!Number.isFinite(count) || count < 0) return null;
  return Math.floor(count);
};

type PageMeta = {
  page: number;
  hasMore: boolean;
  totalItems: number;
};

type InboxPagination = {
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  page?: number;
  pageSize?: number;
};

const emptyPageMeta = (): PageMeta => ({
  page: 1,
  hasMore: false,
  totalItems: 0,
});

const pageMetaFrom = (
  pagination: InboxPagination | undefined,
  fetchedCount: number,
  requestedPage: number
): PageMeta => {
  const page =
    Number(pagination?.currentPage || pagination?.page || requestedPage) ||
    requestedPage;
  const pageSize = Number(pagination?.pageSize || PAGE_SIZE) || PAGE_SIZE;
  const totalItems = Number(pagination?.totalItems ?? fetchedCount) || 0;
  const totalPages =
    Number(pagination?.totalPages) ||
    (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0);
  if (fetchedCount === 0) {
    return { page, hasMore: false, totalItems };
  }
  return {
    page,
    hasMore: page < totalPages || (fetchedCount >= pageSize && page * pageSize < totalItems),
    totalItems,
  };
};

const mergeByKey = <T,>(
  prev: T[],
  next: T[],
  keyOf: (item: T) => string
): T[] => {
  if (!next.length) return prev;
  const map = new Map<string, T>();
  for (const item of prev) {
    const key = keyOf(item);
    if (key) map.set(key, item);
  }
  for (const item of next) {
    const key = keyOf(item);
    if (key) map.set(key, item);
  }
  return Array.from(map.values());
};

const mergeLatestByKey = <T,>(
  prev: T[],
  latest: T[],
  keyOf: (item: T) => string
): T[] => {
  const incomingIds = new Set(latest.map(keyOf).filter(Boolean));
  const rest = prev.filter((item) => {
    const key = keyOf(item);
    return Boolean(key) && !incomingIds.has(key);
  });
  return [...latest, ...rest];
};

const conversationKey = (row: { _id?: string }) => String(row._id || "");
const callRowKey = (row: ICall) => String(row._id || row.sid || "");

const campaignIdOfCall = (call: ICall): string | null => {
  if (typeof call.campaign_id === "string") return call.campaign_id || null;
  return call.campaign_id?._id || null;
};

const campaignNameOfCall = (call: ICall): string => {
  if (call.campaign_id && typeof call.campaign_id === "object") {
    return String(call.campaign_id.campaign_name || "").trim();
  }
  return "";
};

const lineOptionLabel = (line: ILineResponse): string => {
  const number = formatPhone(line.line_number || line.company_number);
  const name = String(line.name || "").trim();
  return name ? `${name} · ${number}` : number;
};

const formatMessageTime = (isoString?: string): string => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatPhone = (value?: string): string => {
  if (!value) return "";
  return value.replace(/^whatsapp:/i, "").trim();
};

const getInitials = (name?: string, phone?: string): string => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  const digits = formatPhone(phone).replace(/\D/g, "");
  return digits.slice(-2) || "?";
};

const channelIcon = (channel: InboxChannel) => {
  if (channel === "call") return <PhoneOutlined />;
  if (channel === "sms") return <MessageOutlined />;
  if (channel === "email") return <MailOutlined />;
  return <WhatsAppOutlined />;
};

const rowKey = (row: InboxRow) => `${row.channel}:${row.id}`;

const MOBILE_INBOX_QUERY = "(max-width: 900px)";

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);

  return matches;
};

const mapCallStatus = (status?: string) => {
  const value = asStatusToken(status);
  if (value === "failed" || value === "canceled" || value === "no-answer") {
    return TICKET_DISPLAY_STATUS.FAILED;
  }
  if (value === "completed" || value === "answered") {
    return TICKET_DISPLAY_STATUS.COMPLETED;
  }
  return TICKET_DISPLAY_STATUS.OPEN;
};

const displayStatusOf = (row: InboxRow): string => ticketDisplayStatusOf(row);

const isNeedsReply = (row: InboxRow): boolean => isNeedsAssign(row);

const isFailedRow = (row: InboxRow): boolean => isFailedTicket(row);

const isClosedInboxRow = (row: InboxRow): boolean => isClosedTicket(row);

const isEscalatedRow = (row: InboxRow): boolean =>
  row.state !== "closed" &&
  displayStatusOf(row) === TICKET_DISPLAY_STATUS.NEED_ASSIGN;

const matchesStatusFilter = (
  row: InboxRow,
  status: string,
  includeClosedOnAll = false
): boolean => {
  const requested = parseInboxStatus(status);
  if (requested === TICKET_DISPLAY_STATUS.FAILED) return isFailedRow(row);
  if (requested === TICKET_DISPLAY_STATUS.NEED_ASSIGN) {
    return isNeedsReply(row) || isEscalatedRow(row);
  }
  if (requested === TICKET_DISPLAY_STATUS.COMPLETED) {
    return isClosedInboxRow(row) && !isFailedRow(row);
  }
  if (requested === TICKET_DISPLAY_STATUS.OPEN) {
    return (
      displayStatusOf(row) === TICKET_DISPLAY_STATUS.OPEN && !isNeedsReply(row)
    );
  }
  if (requested && requested !== TICKET_DISPLAY_STATUS.ALL) {
    return displayStatusOf(row) === requested;
  }
  return includeClosedOnAll ? true : !isClosedInboxRow(row);
};

const isAnsweredCall = (call: ICall): boolean =>
  Number(call.duration || 0) > 0 ||
  ["completed", "answered"].includes(String(call.status || "").toLowerCase());

const parseInboxChannel = (value?: string | null): ChannelFilter => {
  const raw = String(value || "").toLowerCase();
  if (raw === "call" || raw === "sms" || raw === "whatsapp" || raw === "email") {
    return raw;
  }
  return "all";
};

const asInboxChannel = (value?: string): InboxChannel | null => {
  const raw = String(value || "").toLowerCase();
  if (raw === "call" || raw === "sms" || raw === "whatsapp" || raw === "email") {
    return raw;
  }
  return null;
};

const inboxChannelsOf = (
  used: string[] | undefined,
  fallback: InboxChannel
): InboxChannel[] => {
  const fromUsed = (used || [])
    .map(asInboxChannel)
    .filter((channel): channel is InboxChannel => Boolean(channel));
  if (fromUsed.length) {
    return CHANNEL_ORDER.filter((channel) => fromUsed.includes(channel));
  }
  return [fallback];
};

const mergeGroupChannels = (group: InboxRow[]): InboxChannel[] => {
  const seen = new Set<InboxChannel>();
  for (const row of group) {
    for (const channel of row.channels || [row.channel]) {
      seen.add(channel);
    }
  }
  return CHANNEL_ORDER.filter((channel) => seen.has(channel));
};

const identityKey = (row: InboxRow): string => {
  const digits = formatPhone(row.phone).replace(/\D/g, "");
  if (digits.length >= 8) return `p:${digits}`;
  const value = String(row.phone || "").trim().toLowerCase();
  return value ? `e:${value}` : `id:${row.channel}:${row.id}`;
};

const groupRowsByPhone = (rows: InboxRow[]): InboxRow[] => {
  const groups = new Map<string, InboxRow[]>();
  for (const row of rows) {
    const key = identityKey(row);
    const list = groups.get(key) || [];
    list.push(row);
    groups.set(key, list);
  }
  const grouped: InboxRow[][] = [];
  groups.forEach((group) => {
    grouped.push(group);
  });
  return grouped
    .map((group) => {
      const sorted = group.slice().sort((a, b) => b.sortAt - a.sortAt);
      const latest = sorted[0];
      const liveMessaging = sorted.find(
        (row) =>
          (row.channel === "sms" || row.channel === "whatsapp") &&
          !isClosedInboxRow(row)
      );
      const liveTicket =
        liveMessaging ||
        sorted.find((row) => row.channel !== "call" && !isClosedInboxRow(row)) ||
        latest;
      const snippetRow =
        sorted.find((row) => Boolean(row.snippet?.trim())) || latest;
      return {
        ...liveTicket,
        channels: mergeGroupChannels(group),
        snippet: snippetRow.snippet,
        when: latest.when,
        sortAt: latest.sortAt,
      };
    })
    .sort((a, b) => b.sortAt - a.sortAt);
};

const idOf = (value: unknown): string => {
  if (value == null || value === false) return "";
  if (typeof value === "object") {
    const obj = value as { _id?: unknown; id?: unknown };
    return String(obj._id || obj.id || "").trim();
  }
  return String(value).trim();
};

const agentRefOf = (
  value: unknown
): { id: string; name: string } => {
  if (value == null || value === false) return { id: "", name: "" };
  if (typeof value === "object") {
    const obj = value as {
      _id?: unknown;
      id?: unknown;
      agent_name?: unknown;
    };
    return {
      id: String(obj._id || obj.id || "").trim(),
      name: String(obj.agent_name || "").trim(),
    };
  }
  return { id: String(value).trim(), name: "" };
};

const normalizeAssignedTo = (
  value: AssignedToValue
): { role: string; id: string; name: string } | null => {
  if (!value) return null;
  if (typeof value === "string") {
    const id = value.trim();
    return id ? { role: "", id, name: "" } : null;
  }
  const id = idOf(value.id);
  if (!id) return null;
  return {
    role: String(value.role || "").trim(),
    id,
    name: String(value.name || "").trim(),
  };
};

const getAssigneeDisplay = (
  row: InboxRow,
  agentsById: Map<string, IAgent>,
  usersById: Map<string, TeamMember>,
  labels: { needToAssign: string; named: (name: string) => string }
): AssigneeDisplay => {
  const assigned = normalizeAssignedTo(row.assigned_to);

  if (!assigned && row.channel === "call") {
    const callAgent = agentRefOf(row.call?.agent_id);
    const id = callAgent.id || String(row.agentId || "").trim();
    const name =
      callAgent.name ||
      String(row.agentName || "").trim() ||
      String(agentsById.get(id)?.agent_name || "").trim() ||
      id;
    if (id || name) {
      return {
        label: labels.named(name),
        bg: theme.colors.green2,
        fg: theme.colors.green1,
      };
    }
  }

  if (!assigned) {
    return {
      label: labels.needToAssign,
      bg: "#FFEBEE",
      fg: theme.colors.red1,
    };
  }

  const role = assigned.role.toLowerCase();
  if (role === "user") {
    const user = usersById.get(assigned.id);
    const name =
      assigned.name || (user ? teamMemberLabel(user) : "") || assigned.id;
    return {
      label: labels.named(name),
      bg: theme.colors.yellow3,
      fg: theme.colors.yellow4,
    };
  }

  const agent = agentsById.get(assigned.id);
  const name =
    assigned.name ||
    String(agent?.agent_name || "").trim() ||
    (String(row.agentId || "").trim() === assigned.id
      ? String(row.agentName || "").trim()
      : "") ||
    assigned.id;
  return {
    label: labels.named(name),
    bg: theme.colors.green2,
    fg: theme.colors.green1,
  };
};

const mediaSnippet = (message?: {
  content?: string;
  media?: Array<{ kind?: string }>;
  media_url?: string;
}): string => {
  const text = message?.content?.trim();
  if (text) return text;
  const kind = message?.media?.[0]?.kind;
  if (kind === "image") return "[Image]";
  if (kind === "audio") return "[Audio]";
  if (kind === "video") return "[Video]";
  if (kind === "document" || kind === "contact") return "[File]";
  if (message?.media?.length || message?.media_url) return "[Attachment]";
  return "";
};

const getSnippetFromMessages = (
  messages?: {
    role?: string;
    actor?: string;
    content?: string;
    media?: Array<{ kind?: string }>;
    media_url?: string;
  }[]
): string => {
  const lastMessage = [...(messages || [])]
    .reverse()
    .find((msg) => Boolean(mediaSnippet(msg)));
  return mediaSnippet(lastMessage);
};

const conversationSnippet = (conversation: {
  last_message?: IWhatsappMessage | IMailMessage | null;
  messages?: Array<{
    role?: string;
    actor?: string;
    content?: string;
    media?: Array<{ kind?: string }>;
    media_url?: string;
  }>;
}): string =>
  getSnippetFromMessages(conversation.messages) ||
  mediaSnippet(conversation.last_message || undefined);

const Whatsapp: React.FC = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  const { id: paramId } = useParams<{ id: string }>();
  const threadMatch = useMatch("/inbound/whatsapp/:id");
  const id = paramId || threadMatch?.params.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const isCompanyAdmin = user?.role === ROLES.ADMIN;
  const campaignId = searchParams.get("campaign_id") || "";
  const lineId = searchParams.get("line_id") || "";
  const agentId = searchParams.get("agent_id") || "";
  const assignedTo = searchParams.get("assigned_to") || "";
  const handledBy = parseHandledByFilter(searchParams.get("handled_by"));
  const aiOnlyChecked = handledBy === HANDLED_BY_FILTER.AI;
  const ticketIdParam = searchParams.get("ticket_id") || "";
  const assigneeSelectValue = assignedTo
    ? `${ASSIGNEE_USER_PREFIX}${assignedTo}`
    : agentId
      ? `${ASSIGNEE_AGENT_PREFIX}${agentId}`
      : FILTER_ALL;
  const requestedChannel = parseInboxChannel(searchParams.get("channel"));
  const isCampaignView = Boolean(campaignId);
  const status = parseInboxStatus(
    searchParams.get("needs_reply") === "1"
      ? TICKET_DISPLAY_STATUS.ESCALATED
      : searchParams.get("status")
  );
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>(
    requestedChannel
  );
  const { isEnabled, flags } = useChannelSettings();
  const visibleFilters = CHANNEL_FILTERS.filter(
    (filter) => filter === "all" || isEnabled(filter)
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(
    id ? `whatsapp:${id}` : null
  );
  const [selectedTimeRange, setSelectedTimeRange] = useState("7d");
  const [whatsappRows, setWhatsappRows] = useState<IWhatsappConversation[]>([]);
  const [mailRows, setMailRows] = useState<IMailConversation[]>([]);
  const [smsRows, setSmsRows] = useState<IWhatsappConversation[]>([]);
  const [inboundCalls, setInboundCalls] = useState<ICall[]>([]);
  const [channelMeta, setChannelMeta] = useState<Record<InboxChannel, PageMeta>>({
    call: emptyPageMeta(),
    sms: emptyPageMeta(),
    whatsapp: emptyPageMeta(),
    email: emptyPageMeta(),
  });
  const [serverNeedsReplyCount, setServerNeedsReplyCount] = useState<
    number | null
  >(null);
  const [listLoading, setListLoading] = useState(true);
  const [linesReady, setLinesReady] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [mobileThreadOpen, setMobileThreadOpen] = useState(Boolean(id || ticketIdParam));
  const [inboundAgents, setInboundAgents] = useState<IAgent[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [assigningTicketId, setAssigningTicketId] = useState<string | null>(
    null
  );
  const agentsById = useMemo(() => {
    const map = new Map<string, IAgent>();
    for (const agent of inboundAgents) {
      const id = String(agent?._id || "").trim();
      if (id) map.set(id, agent);
    }
    return map;
  }, [inboundAgents]);
  const usersById = useMemo(() => {
    const map = new Map<string, TeamMember>();
    for (const user of teamMembers) {
      const id = String(user?._id || "").trim();
      if (id) map.set(id, user);
    }
    return map;
  }, [teamMembers]);
  const isMobile = useMediaQuery(MOBILE_INBOX_QUERY);
  const [otherThread, setOtherThread] = useState<IWhatsappConversation | null>(
    null
  );
  const [otherThreadLoading, setOtherThreadLoading] = useState(false);
  const listBodyRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreLockRef = useRef(false);
  const loadMoreRef = useRef<() => void>(() => undefined);
  const fetchGenerationRef = useRef(0);
  const pollInFlightRef = useRef(false);

  const campaignList =
    useAppSelector((state) => state.campaign.campaigns?.campaigns) || [];
  const lineList = useAppSelector((state) => state.line.lines?.lines) || [];
  const campaignNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const campaign of campaignList as { _id?: string; campaign_name?: string }[]) {
      const key = String(campaign?._id || "");
      const name = String(campaign?.campaign_name || "").trim();
      if (key && name) map.set(key, name);
    }
    return map;
  }, [campaignList]);

  const loadChannel = (channel: InboxChannel) =>
    isEnabled(channel) &&
    (requestedChannel === "all" || requestedChannel === channel);

  const applyStatusFilter = (nextStatus: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const token = parseInboxStatus(nextStatus);
        if (token === TICKET_DISPLAY_STATUS.ALL) next.delete("status");
        else next.set("status", token);
        next.delete("needs_reply");
        return next;
      },
      { replace: true }
    );
  };

  const inboxNavigate = (
    path: string,
    extra?: Record<string, string | null>
  ) => {
    const params = new URLSearchParams(searchParams);
    if (extra) {
      Object.entries(extra).forEach(([key, value]) => {
        if (value == null || value === "") params.delete(key);
        else params.set(key, value);
      });
    }
    const qs = params.toString();
    const next = qs ? `${path}?${qs}` : path;
    if (`${window.location.pathname}${window.location.search}` !== next) {
      navigate(next, { replace: true });
    }
  };

  const applyInboxFilters = (patch: {
    campaign_id?: string | null;
    line_id?: string | null;
    agent_id?: string | null;
    assigned_to?: string | null;
    handled_by?: HandledByFilter | null;
  }) => {
    const nextHandledBy =
      patch.handled_by === undefined ? handledBy : patch.handled_by;
    let nextAgentId =
      patch.agent_id === undefined ? agentId || null : patch.agent_id;
    let nextAssignedTo =
      patch.assigned_to === undefined ? assignedTo || null : patch.assigned_to;
    // AI-only mode is agent-scoped; drop team-member assignee picks.
    if (nextHandledBy === HANDLED_BY_FILTER.AI) nextAssignedTo = null;
    inboxNavigate("/inbound", {
      campaign_id:
        patch.campaign_id === undefined ? campaignId || null : patch.campaign_id,
      line_id: patch.line_id === undefined ? lineId || null : patch.line_id,
      agent_id: nextAgentId,
      assigned_to: nextAssignedTo,
      handled_by:
        nextHandledBy === HANDLED_BY_FILTER.AI ? HANDLED_BY_FILTER.AI : null,
      ticket_id: null,
    });
  };

  useEffect(() => {
    dispatch(
      getCampaignsThunk({
        page: 1,
        limit: 999,
        keyword: "",
        status: "",
        directionFilter: "",
        sort: { createdAt: -1 },
      })
    );
    dispatch(
      getLinesThunk({
        limit: 999,
        page: 1,
        line_number: "",
      })
    )
      .unwrap()
      .catch(() => undefined)
      .finally(() => setLinesReady(true));
    getAgents({
      limit: 999,
      page: 1,
      keyword: "",
    })
      .then((res) => {
        setInboundAgents(res.data?.data?.agents || []);
      })
      .catch(() => {
        setInboundAgents([]);
      });
    getUsers({ page: 1, limit: 999, status: 1 })
      .then((res) => {
        const payload = res.data?.data;
        const rows = (payload?.data ||
          payload?.users ||
          (Array.isArray(payload) ? payload : []) ||
          []) as TeamMember[];
        setTeamMembers(
          (Array.isArray(rows) ? rows : []).filter((user) => {
            const id = String(user?._id || "");
            return id && !user.deleted_at;
          })
        );
      })
      .catch(() => {
        setTeamMembers([]);
      });
  }, [dispatch]);

  useEffect(() => {
    if (isCampaignView && requestedChannel !== "all") {
      setChannelFilter(requestedChannel);
    }
  }, [isCampaignView, campaignId, requestedChannel]);

  const startDate = useMemo(() => {
    const now = new Date();
    const days =
      selectedTimeRange === "24h"
        ? 1
        : selectedTimeRange === "30d"
          ? 30
          : selectedTimeRange === "90d"
            ? 90
            : 7;
    now.setDate(now.getDate() - days);
    return now.toISOString().slice(0, 10);
  }, [selectedTimeRange]);

  useEffect(() => {
    if (channelFilter !== "all" && !isEnabled(channelFilter)) {
      setChannelFilter("all");
    }
  }, [channelFilter, flags, isEnabled]);

  useEffect(() => {
    if (!isCampaignView && isEnabled("whatsapp")) {
      dispatch(getWhatsappStatsThunk({ start_time: startDate }));
    }
  }, [dispatch, startDate, flags, isCampaignView]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchInput.trim());
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const apiStatus =
    status === TICKET_DISPLAY_STATUS.ALL ? "" : asStatusToken(status);

  const inboxQueryFor = (page: number) => ({
    page,
    limit: PAGE_SIZE,
    keyword: debouncedSearchTerm,
    status: apiStatus,
    start_time: startDate,
    ...(campaignId ? { campaign_id: campaignId } : {}),
    ...(lineId ? { line_id: lineId } : {}),
    ...(agentId ? { agent_id: agentId } : {}),
    ...(assignedTo ? { assigned_to: assignedTo } : {}),
    ...(handledBy !== HANDLED_BY_FILTER.ALL ? { handled_by: handledBy } : {}),
  });

  const fetchChannelPages = async (
    pages: Partial<Record<InboxChannel, number>>,
    append: boolean,
    cancelled: () => boolean,
    refresh = false
  ) => {
    const tasks: Promise<void>[] = [];
    const applyRows = <T,>(
      prev: T[],
      next: T[],
      keyOf: (item: T) => string
    ) => {
      if (append) return mergeByKey(prev, next, keyOf);
      if (refresh) return mergeLatestByKey(prev, next, keyOf);
      return next;
    };
    const applyMeta = (
      channel: InboxChannel,
      meta: PageMeta,
      fetchedCount: number
    ) => {
      setChannelMeta((prev) => ({
        ...prev,
        [channel]: refresh
          ? { ...prev[channel], totalItems: meta.totalItems }
          : append && fetchedCount === 0
            ? { ...meta, hasMore: false }
            : meta,
      }));
    };

    const fetchMessages = <T extends { _id?: string }>(
      channel: "whatsapp" | "sms" | "email",
      apiChannel: string,
      setRows: React.Dispatch<React.SetStateAction<T[]>>
    ) => {
      const page = pages[channel];
      if (!page) return;
      tasks.push(
        getInboxConversations(apiChannel, inboxQueryFor(page))
          .then((res) => {
            if (cancelled()) return;
            const messages = (res.data?.data?.messages || []) as T[];
            const meta = pageMetaFrom(
              res.data?.data?.pagination,
              messages.length,
              page
            );
            setRows((prev) => applyRows(prev, messages, conversationKey));
            applyMeta(channel, meta, messages.length);
          })
          .catch(() => {
            if (cancelled() || refresh) return;
            if (!append) setRows([]);
            setChannelMeta((prev) => ({
              ...prev,
              [channel]: {
                page,
                hasMore: false,
                totalItems: append ? prev[channel].totalItems : 0,
              },
            }));
          })
      );
    };

    if (loadChannel("whatsapp")) {
      fetchMessages("whatsapp", "WHATSAPP_MESSAGE", setWhatsappRows);
    } else if (!append && !refresh) {
      setWhatsappRows([]);
      setChannelMeta((prev) => ({ ...prev, whatsapp: emptyPageMeta() }));
    }

    if (loadChannel("email")) {
      fetchMessages("email", "EMAIL", setMailRows);
    } else if (!append && !refresh) {
      setMailRows([]);
      setChannelMeta((prev) => ({ ...prev, email: emptyPageMeta() }));
    }

    if (loadChannel("sms")) {
      fetchMessages("sms", "SMS", setSmsRows);
    } else if (!append && !refresh) {
      setSmsRows([]);
      setChannelMeta((prev) => ({ ...prev, sms: emptyPageMeta() }));
    }

    // Calls are AI-agent scoped only; team-member assignee filter skips them.
    if (pages.call && loadChannel("call") && !assignedTo) {
      const page = pages.call;
      tasks.push(
        getCalls({
          page,
          limit: PAGE_SIZE,
          start_date: startDate,
          ...(campaignId ? { campaign_id: campaignId } : {}),
          ...(lineId ? { line_id: lineId } : {}),
          ...(agentId ? { agent_id: agentId } : {}),
          ...(!campaignId ? { direction: "inbound" as const } : {}),
        })
          .then((res) => {
            if (cancelled()) return;
            const data = res.data?.data;
            const calls = data?.calls || [];
            const meta = pageMetaFrom(data?.pagination, calls.length, page);
            setInboundCalls((prev) => applyRows(prev, calls, callRowKey));
            applyMeta("call", meta, calls.length);
          })
          .catch(() => {
            if (cancelled() || refresh) return;
            if (!append) setInboundCalls([]);
            setChannelMeta((prev) => ({
              ...prev,
              call: {
                page,
                hasMore: false,
                totalItems: append ? prev.call.totalItems : 0,
              },
            }));
          })
      );
    } else if (!append && !refresh) {
      setInboundCalls([]);
      setChannelMeta((prev) => ({ ...prev, call: emptyPageMeta() }));
    }

    await Promise.all(tasks);
  };

  // The time-range selector previously only drove the stats query, so the list
  // silently ignored it — the UI claimed to be filtered and wasn't.
  useEffect(() => {
    let cancelled = false;
    fetchGenerationRef.current += 1;
    const generation = fetchGenerationRef.current;
    setListLoading(true);
    setDisplayCount(PAGE_SIZE);
    const pages: Partial<Record<InboxChannel, number>> = {};
    (["whatsapp", "email", "sms", "call"] as InboxChannel[]).forEach(
      (channel) => {
        if (loadChannel(channel)) pages[channel] = 1;
      }
    );
    fetchChannelPages(
      pages,
      false,
      () => cancelled || generation !== fetchGenerationRef.current
    ).finally(() => {
      if (!cancelled && generation === fetchGenerationRef.current) {
        setListLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchTerm,
    apiStatus,
    startDate,
    flags,
    campaignId,
    lineId,
    agentId,
    assignedTo,
    handledBy,
    requestedChannel,
  ]);

  // Header "need a reply" is the full matching set. The list stays paginated;
  // this query ignores the status dropdown and page size so the total does not
  // shrink to the current page or to open/completed rows.
  useEffect(() => {
    let cancelled = false;
    const apiChannel = needsReplyApiChannel(requestedChannel, isEnabled);
    if (!apiChannel) {
      setServerNeedsReplyCount(0);
      return;
    }
    getInboxConversations(apiChannel, {
      page: 1,
      limit: 1,
      keyword: debouncedSearchTerm,
      status: "",
      start_time: startDate,
      ...(campaignId ? { campaign_id: campaignId } : {}),
      ...(lineId ? { line_id: lineId } : {}),
      ...(agentId ? { agent_id: agentId } : {}),
      ...(assignedTo ? { assigned_to: assignedTo } : {}),
      ...(handledBy !== HANDLED_BY_FILTER.ALL ? { handled_by: handledBy } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        const payload = res.data?.data || res.data;
        setServerNeedsReplyCount(asNeedsReplyCount(payload?.needsReplyCount));
      })
      .catch(() => {
        if (!cancelled) setServerNeedsReplyCount(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchTerm,
    startDate,
    flags,
    campaignId,
    lineId,
    agentId,
    assignedTo,
    handledBy,
    requestedChannel,
  ]);

  useThreadPolling(
    () => {
      if (listLoading || loadingMore || pollInFlightRef.current) return;
      if (searchInput.trim() !== debouncedSearchTerm) return;

      const generation = fetchGenerationRef.current;
      const cancelled = () => generation !== fetchGenerationRef.current;
      const listEl = listBodyRef.current;
      const stickToTop = !listEl || listEl.scrollTop < 48;
      const prevScroll = listEl?.scrollTop ?? 0;
      const prevHeight = listEl?.scrollHeight ?? 0;
      const pages: Partial<Record<InboxChannel, number>> = {};
      (["whatsapp", "email", "sms", "call"] as InboxChannel[]).forEach(
        (channel) => {
          if (loadChannel(channel)) pages[channel] = 1;
        }
      );

      const apiChannel = needsReplyApiChannel(requestedChannel, isEnabled);
      const needsReplyTask = apiChannel
        ? getInboxConversations(apiChannel, {
            page: 1,
            limit: 1,
            keyword: debouncedSearchTerm,
            status: "",
            start_time: startDate,
            ...(campaignId ? { campaign_id: campaignId } : {}),
            ...(lineId ? { line_id: lineId } : {}),
            ...(agentId ? { agent_id: agentId } : {}),
            ...(assignedTo ? { assigned_to: assignedTo } : {}),
            ...(handledBy !== HANDLED_BY_FILTER.ALL
              ? { handled_by: handledBy }
              : {}),
          })
            .then((res) => {
              if (cancelled()) return;
              const payload = res.data?.data || res.data;
              setServerNeedsReplyCount(
                asNeedsReplyCount(payload?.needsReplyCount)
              );
            })
            .catch(() => undefined)
        : Promise.resolve();

      pollInFlightRef.current = true;
      Promise.all([
        fetchChannelPages(pages, false, cancelled, true),
        needsReplyTask,
      ])
        .then(() => {
          if (cancelled() || stickToTop || !listBodyRef.current) return;
          const next = listBodyRef.current;
          next.scrollTop = prevScroll + (next.scrollHeight - prevHeight);
        })
        .finally(() => {
          pollInFlightRef.current = false;
        });
    },
    {
      enabled: !listLoading,
      intervalMs: INBOX_POLL_MS,
      backoff: false,
    }
  );

  const channelLabel = (channel: InboxChannel) => {
    if (channel === "call") return t("dashboard.v2.phoneCall");
    if (channel === "sms") return t("dashboard.v2.textMessage");
    if (channel === "email") return t("dashboard.v2.email");
    return t("dashboard.v2.whatsapp");
  };

  const allRows = useMemo<InboxRow[]>(() => {
    const whatsapp = (loadChannel("whatsapp") ? whatsappRows : []).map(
      (conversation) => ({
        id: conversation._id,
        channel: "whatsapp" as const,
        phone: formatPhone(conversation.user_phone_number),
        name: conversation.recipient_name || "",
        snippet: conversationSnippet(conversation),
        when: formatMessageTime(conversation.last_user_message_at),
        sortAt: new Date(
          conversation.last_user_message_at || conversation.updatedAt || 0
        ).getTime(),
        status: conversation.status,
        internal_status: conversation.internal_status,
        handling_mode: conversation.handling_mode,
        awaiting: conversation.awaiting,
        state: conversation.state,
        assigned_to: conversation.assigned_to,
        escalated_at: conversation.escalated_at,
        resolution: conversation.resolution,
        summary: conversation.summary,
        ticketId: conversation._id,
        agentId: agentRefOf(conversation.agent_id).id || null,
        agentName: agentRefOf(conversation.agent_id).name || null,
        campaignId: conversation.campaign_id || null,
        campaignName:
          String(conversation.campaign_name || "").trim() ||
          campaignNameById.get(String(conversation.campaign_id || "")) ||
          "",
        channels: inboxChannelsOf(conversation.channels_used, "whatsapp"),
      })
    );

    const ticketIds = new Set([
      ...whatsapp.map((row) => String(row.ticketId || row.id)),
      ...(loadChannel("sms") ? smsRows : []).map((row) => String(row._id || "")),
    ]);

    const calls = (loadChannel("call") ? inboundCalls : [])
      .filter((call) => {
        if (isCampaignView && !isAnsweredCall(call)) return false;
        const linkedTicket = call.ticket_id ? String(call.ticket_id) : "";
        if (linkedTicket && ticketIds.has(linkedTicket)) return false;
        return true;
      })
      .map((call) => ({
        id: call._id || call.sid,
        channel: "call" as const,
        phone: formatPhone(
          call.direction === "outbound" ? call.to : call.from
        ),
        name: call.recipient_name || call.agent_id?.agent_name || "",
        snippet: call.summary || "",
        when: formatMessageTime(call.start_time || call.lastUpdated),
        sortAt: new Date(call.start_time || call.lastUpdated || 0).getTime(),
        status: mapCallStatus(call.status),
        handling_mode: "ai",
        summary: call.summary,
        ticketId: call.ticket_id || null,
        agentId: agentRefOf(call.agent_id).id || null,
        agentName: agentRefOf(call.agent_id).name || null,
        campaignId: campaignIdOfCall(call),
        campaignName:
          campaignNameOfCall(call) ||
          campaignNameById.get(String(campaignIdOfCall(call) || "")) ||
          "",
        call,
        channels: ["call"] as InboxChannel[],
      }));

    const mail = (loadChannel("email") ? mailRows : [])
      .filter((conversation) => {
        const channels = conversation.channels_used;
        if (Array.isArray(channels) && channels.length > 0) {
          return channels.some((item) => String(item).toLowerCase() === "email");
        }
        return Boolean(conversation.user_email);
      })
      .map((conversation) => ({
        id: conversation._id,
        channel: "email" as const,
        phone: conversation.user_email || "",
        name: conversation.recipient_name || "",
        snippet: conversationSnippet(conversation),
        when: formatMessageTime(conversation.last_user_message_at),
        sortAt: new Date(
          conversation.last_user_message_at ||
            conversation.updatedAt ||
            conversation.updated_at ||
            0
        ).getTime(),
        status: conversation.status,
        internal_status: conversation.internal_status,
        handling_mode: conversation.handling_mode,
        awaiting: conversation.awaiting,
        state: conversation.state,
        assigned_to: conversation.assigned_to,
        escalated_at: conversation.escalated_at,
        resolution: conversation.resolution,
        summary: conversation.summary,
        ticketId: conversation._id,
        agentId: agentRefOf(conversation.agent_id).id || null,
        agentName: agentRefOf(conversation.agent_id).name || null,
        campaignId: conversation.campaign_id || null,
        campaignName:
          String(conversation.campaign_name || "").trim() ||
          campaignNameById.get(String(conversation.campaign_id || "")) ||
          "",
        mail: conversation,
        channels: inboxChannelsOf(conversation.channels_used, "email"),
      }));

    const sms = (loadChannel("sms") ? smsRows : []).map((conversation) => ({
      id: conversation._id,
      channel: "sms" as const,
      phone: formatPhone(conversation.user_phone_number),
      name: conversation.recipient_name || "",
      snippet: conversationSnippet(conversation),
      when: formatMessageTime(conversation.last_user_message_at),
      sortAt: new Date(
        conversation.last_user_message_at || conversation.updatedAt || 0
      ).getTime(),
      status: conversation.status,
      internal_status: conversation.internal_status,
      handling_mode: conversation.handling_mode,
      awaiting: conversation.awaiting,
      state: conversation.state,
      assigned_to: conversation.assigned_to,
      escalated_at: conversation.escalated_at,
      resolution: conversation.resolution,
      summary: conversation.summary,
      ticketId: conversation._id,
      agentId: agentRefOf(conversation.agent_id).id || null,
      agentName: agentRefOf(conversation.agent_id).name || null,
      campaignId: conversation.campaign_id || null,
        campaignName:
          String(conversation.campaign_name || "").trim() ||
          campaignNameById.get(String(conversation.campaign_id || "")) ||
          "",
        sms: conversation,
        channels: inboxChannelsOf(conversation.channels_used, "sms"),
      }));

    const query = debouncedSearchTerm.toLowerCase();
    const merged = [...whatsapp, ...calls, ...mail, ...sms].filter((row) => {
      if (!isEnabled(row.channel)) return false;
      if (isCampaignView && campaignId) {
        if (String(row.campaignId || "") !== campaignId) return false;
      }
      if (!query) return true;
      return (
        row.phone.toLowerCase().includes(query) ||
        row.name.toLowerCase().includes(query)
      );
    });
    return merged.sort((a, b) => b.sortAt - a.sortAt);
  }, [
    whatsappRows,
    inboundCalls,
    mailRows,
    smsRows,
    debouncedSearchTerm,
    flags,
    campaignId,
    isCampaignView,
    requestedChannel,
    campaignNameById,
  ]);

  const channelRows = allRows.filter(
    (row) => channelFilter === "all" || row.channel === channelFilter
  );

  const filteredRows = channelRows.filter(
    (row) =>
      matchesStatusFilter(row, status, isCampaignView) &&
      matchesHandledByFilter(row, handledBy)
  );
  const visibleRows = groupRowsByPhone(filteredRows);
  const pagedRows = visibleRows.slice(0, displayCount);
  const hasLocalMore = displayCount < visibleRows.length;
  const remoteChannels = (
    ["call", "sms", "whatsapp", "email"] as InboxChannel[]
  ).filter(
    (channel) =>
      isEnabled(channel) &&
      (channelFilter === "all" || channelFilter === channel) &&
      channelMeta[channel].hasMore
  );
  const hasRemoteMore = remoteChannels.length > 0;
  const canLoadMore = hasLocalMore || hasRemoteMore;
  const visibleKeys = useMemo(
    () => visibleRows.map(rowKey).join("|"),
    [visibleRows]
  );

  const loadedNeedsReplyCount = groupRowsByPhone(
    allRows.filter((row) => isNeedsReply(row) || isEscalatedRow(row))
  ).length;
  const needsReplyCount =
    serverNeedsReplyCount == null
      ? loadedNeedsReplyCount
      : serverNeedsReplyCount;

  const countForChannel = (key: ChannelFilter) => {
    if (status === TICKET_DISPLAY_STATUS.ESCALATED) {
      const pool = allRows.filter((row) =>
        matchesStatusFilter(row, status, isCampaignView)
      );
      if (key === "all") return groupRowsByPhone(pool).length;
      return groupRowsByPhone(
        pool.filter(
          (row) => row.channel === key || row.channels?.includes(key)
        )
      ).length;
    }
    if (key !== "all" && channelMeta[key]?.totalItems) {
      return channelMeta[key].totalItems;
    }
    const pool = allRows.filter((row) =>
      matchesStatusFilter(row, status, isCampaignView)
    );
    if (key === "all") return groupRowsByPhone(pool).length;
    return groupRowsByPhone(
      pool.filter((row) => row.channel === key || row.channels?.includes(key))
    ).length;
  };

  useEffect(() => {
    setDisplayCount(PAGE_SIZE);
  }, [channelFilter, status, debouncedSearchTerm, handledBy]);

  useEffect(() => {
    if (!selectedKey) return;
    const index = visibleRows.findIndex((row) => rowKey(row) === selectedKey);
    if (index >= displayCount) {
      setDisplayCount(Math.ceil((index + 1) / PAGE_SIZE) * PAGE_SIZE);
    }
  }, [selectedKey, visibleRows, displayCount]);

  const selectRow = (row: InboxRow) => {
    setSelectedKey(rowKey(row));
    setMobileThreadOpen(true);
    if (isCampaignView) {
      inboxNavigate("/inbound", {
        campaign_id: campaignId,
        channel: requestedChannel === "all" ? null : requestedChannel,
        ticket_id: row.ticketId || row.id,
      });
      return;
    }
    if (row.channel === "whatsapp") {
      if (id !== row.id) {
        inboxNavigate(`/inbound/whatsapp/${row.id}`);
      }
      return;
    }
    if (id) {
      inboxNavigate("/inbound");
    }
  };

  const closeMobileThread = () => {
    setMobileThreadOpen(false);
    if (isCampaignView) return;
    if (id) inboxNavigate("/inbound");
  };

  useEffect(() => {
    if (id) {
      setSelectedKey(`whatsapp:${id}`);
      setMobileThreadOpen(true);
    }
  }, [id]);

  const busy = listLoading;

  // Missing line flags count as off. Legacy readers treat an unset SMS flag as
  // on; this prompt should appear until SMS or WhatsApp is explicitly enabled.
  const messagingNotConfigured = useMemo(() => {
    if (!linesReady) return false;
    const smsOn =
      isEnabled("sms") && lineList.some((line) => line.sms_enabled === true);
    const whatsappOn =
      isEnabled("whatsapp") &&
      lineList.some((line) => line.whatsapp_enabled === true);
    return !smsOn && !whatsappOn;
  }, [linesReady, lineList, flags, isEnabled]);
  const inboxListEmpty =
    pagedRows.length === 0 && !loadingMore && !canLoadMore && !busy;
  const showMessagingPrompt = inboxListEmpty && messagingNotConfigured;

  useEffect(() => {
    if (busy) return;
    const matchesTicket = (row: InboxRow) =>
      Boolean(ticketIdParam) &&
      (row.id === ticketIdParam || row.ticketId === ticketIdParam);
    const fromUrl =
      (ticketIdParam && visibleRows.find(matchesTicket)) ||
      (ticketIdParam && allRows.find(matchesTicket)) ||
      null;
    if (fromUrl) {
      if (selectedKey !== rowKey(fromUrl)) {
        setSelectedKey(rowKey(fromUrl));
        setMobileThreadOpen(true);
      }
      return;
    }
    const keys = visibleKeys ? visibleKeys.split("|") : [];
    const stillVisible = Boolean(selectedKey && keys.includes(selectedKey));
    if (stillVisible) return;
    // Deep-linked ticket may still be on a later page — wait for more rows.
    if ((ticketIdParam || id) && hasRemoteMore) return;
    // On small screens keep the list first; do not auto-open a thread.
    if (isMobile && !id && !ticketIdParam) {
      setSelectedKey(null);
      setMobileThreadOpen(false);
      return;
    }
    if (visibleRows[0]) {
      selectRow(visibleRows[0]);
    } else if (selectedKey) {
      setSelectedKey(null);
      setMobileThreadOpen(false);
      if (id && !isCampaignView) inboxNavigate("/inbound");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    busy,
    channelFilter,
    status,
    visibleKeys,
    isMobile,
    ticketIdParam,
  ]);

  const selectedRow =
    visibleRows.find((row) => rowKey(row) === selectedKey) ||
    allRows.find((row) => rowKey(row) === selectedKey) ||
    null;

  const applyConversationPatch = (
    patch: Partial<IWhatsappConversation> & { _id: string }
  ) => {
    const applyWhatsapp = (rows: IWhatsappConversation[]) =>
      rows.map((row) =>
        String(row._id) === String(patch._id) ? { ...row, ...patch } : row
      );
    const applyMail = (rows: IMailConversation[]) =>
      rows.map((row) =>
        String(row._id) === String(patch._id)
          ? ({ ...row, ...patch } as IMailConversation)
          : row
      );
    setWhatsappRows(applyWhatsapp);
    setSmsRows(applyWhatsapp);
    setMailRows(applyMail);
  };

  const canAssignTicket = (row: InboxRow) =>
    isCompanyAdmin &&
    row.channel !== "call" &&
    Boolean(row.ticketId || row.id) &&
    isNeedsAssign(row);

  const handleAssignTicket = async (row: InboxRow, memberId: string) => {
    const ticketId = String(row.ticketId || row.id || "").trim();
    if (!ticketId || !memberId || assigningTicketId) return;
    const member = usersById.get(memberId);
    const memberName = member ? teamMemberLabel(member) : memberId;
    setAssigningTicketId(ticketId);
    try {
      await assignWhatsappConversation(ticketId, memberId);
      applyConversationPatch({
        _id: ticketId,
        status: TICKET_DISPLAY_STATUS.OPEN,
        state: TICKET_STATE.OPEN,
        handling_mode: "human",
        awaiting: "us",
        assigned_to: {
          role: "user",
          id: memberId,
          name: memberName,
          source: "human_takeover",
        },
      });
      toast.success(t("inbound.whatsapp.assignedSuccessfully"), 3);
    } catch {
      toast.error(t("inbound.whatsapp.failedToAssign"), 5);
    } finally {
      setAssigningTicketId(null);
    }
  };

  useEffect(() => {
    if (
      !selectedRow ||
      selectedRow.channel === "whatsapp" ||
      selectedRow.channel === "sms" ||
      selectedRow.channel === "call"
    ) {
      setOtherThread(null);
      setOtherThreadLoading(false);
      return;
    }
    const threadId = selectedRow.ticketId || selectedRow.id;
    let cancelled = false;
    setOtherThreadLoading(true);
    getWhatsappConversationById(
      threadId,
      false,
      campaignId ? { campaign_id: campaignId } : undefined
    )
      .then((res) => {
        if (cancelled) return;
        setOtherThread(res.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setOtherThread(null);
      })
      .finally(() => {
        if (!cancelled) setOtherThreadLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRow?.id, selectedRow?.channel, selectedRow?.ticketId, campaignId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadMoreRef.current = () => {
      if (listLoading || loadingMore || loadMoreLockRef.current) return;
      if (hasLocalMore) {
        setDisplayCount((count) => count + PAGE_SIZE);
        return;
      }
      if (!hasRemoteMore) return;
      const generation = fetchGenerationRef.current;
      const pages: Partial<Record<InboxChannel, number>> = {};
      remoteChannels.forEach((channel) => {
        pages[channel] = (channelMeta[channel].page || 1) + 1;
      });
      if (!Object.keys(pages).length) return;
      loadMoreLockRef.current = true;
      setLoadingMore(true);
      fetchChannelPages(pages, true, () => generation !== fetchGenerationRef.current)
        .then(() => {
          if (generation !== fetchGenerationRef.current) return;
          setDisplayCount((count) => count + PAGE_SIZE);
        })
        .finally(() => {
          if (generation === fetchGenerationRef.current) {
            setLoadingMore(false);
          }
          loadMoreLockRef.current = false;
        });
    };
  });

  useEffect(() => {
    const root = listBodyRef.current;
    const target = loadMoreSentinelRef.current;
    if (!root || !target || !canLoadMore || listLoading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMoreRef.current();
        }
      },
      { root, rootMargin: "180px", threshold: 0 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [canLoadMore, pagedRows.length, listLoading, loadingMore, channelFilter]);

  const renderOtherThread = (row: InboxRow) => {
    const ticketMessages =
      otherThread && otherThread._id === (row.ticketId || row.id)
        ? otherThread.messages
        : row.channel === "email"
          ? row.mail?.messages
          : row.channel === "sms"
            ? row.sms?.messages
            : undefined;
    const messages = ticketMessages
      ? ticketMessages.map((message) => ({
          role: message.role,
          text: message.content,
          at: message.created_at,
        }))
      : row.call
        ? Array.isArray(row.call.transcript)
          ? row.call.transcript.map((item) => ({
              role: item.role === "user" ? "user" : "assistant",
              text: item.message,
              at: item.updated_at
                ? new Date(item.updated_at).toISOString()
                : row.call?.start_time,
            }))
          : [
              {
                role: "assistant",
                text:
                  (typeof row.call.transcript === "string" &&
                    row.call.transcript) ||
                  row.summary ||
                  "",
                at: row.call.start_time,
              },
            ].filter((item) => item.text)
        : [];

    return (
      <OtherThread>
        <OtherHead>
          <OtherIdentity>
            <strong>{row.phone || "—"}</strong>
            <span>{row.name || "—"}</span>
          </OtherIdentity>
          <Muted>{channelLabel(row.channel)}</Muted>
        </OtherHead>
        {row.summary ? <SummaryBox>{row.summary}</SummaryBox> : null}
        <OtherBody>
          {otherThreadLoading &&
          (row.channel === "sms" || row.channel === "email") ? (
            <EmptyCopy>
              <LoadingOutlined />
            </EmptyCopy>
          ) : messages.length === 0 ? (
            <EmptyCopy>{t("inbound.whatsapp.noMessages")}</EmptyCopy>
          ) : (
            messages.map((message, index) => (
              <Bubble key={`${row.id}-${index}`} $out={message.role !== "user"}>
                {message.text}
                {message.at ? <When>{formatMessageTime(String(message.at))}</When> : null}
              </Bubble>
            ))
          )}
        </OtherBody>
      </OtherThread>
    );
  };

  return (
    <Page>
      <Header>
        <TitleBlock>
          <Title>{t("inbound.title")}</Title>
          <HeaderMeta>
            {t("inbound.whatsapp.conversationCount", {
              count: countForChannel("all"),
            })}
            {" · "}
            <NeedsReplyLink
              type="button"
              onClick={() => {
                setChannelFilter("all");
                applyStatusFilter(TICKET_DISPLAY_STATUS.ESCALATED);
              }}
            >
              {t("inbound.whatsapp.needAReply", { count: needsReplyCount })}
            </NeedsReplyLink>
          </HeaderMeta>
        </TitleBlock>
        <HeaderFilters>
          <FilterSelect
            value={selectedTimeRange}
            onChange={(value: unknown) => setSelectedTimeRange(value as string)}
            suffixIcon={busy ? <LoadingOutlined /> : <DownOutlined />}
            loading={busy}
            popupMatchSelectWidth={false}
            getPopupContainer={() => document.body}
            dropdownStyle={{ zIndex: 1200 }}
            aria-label={t("dashboard.filters.last7d")}
            $width={180}
          >
            <Option value="24h">{t("dashboard.filters.last24h")}</Option>
            <Option value="7d">{t("dashboard.filters.last7d")}</Option>
            <Option value="30d">{t("dashboard.filters.last30d")}</Option>
            <Option value="90d">{t("dashboard.filters.last90d")}</Option>
          </FilterSelect>
        </HeaderFilters>
      </Header>

      <FilterBar>
        <BarSelect
          value={channelFilter}
          onChange={(value: unknown) => {
            setChannelFilter(parseInboxChannel(String(value)));
          }}
          suffixIcon={<DownOutlined />}
          popupMatchSelectWidth={false}
          getPopupContainer={() => document.body}
          dropdownStyle={{ zIndex: 1200 }}
          aria-label={t("inbound.whatsapp.filterChannel")}
          $width={210}
          $stretch
        >
          {visibleFilters.map((filter) => {
            const label =
              filter === "all"
                ? t("inbound.whatsapp.allChannels")
                : channelLabel(filter);
            return (
              <Option key={filter} value={filter} title={label}>
                <ChannelOption>
                  {filter !== "all" ? channelIcon(filter) : null}
                  <span>{label}</span>
                  <ChannelCount>{countForChannel(filter)}</ChannelCount>
                </ChannelOption>
              </Option>
            );
          })}
        </BarSelect>
        <FilterBarTools>
          <BarSelect
            value={lineId || FILTER_ALL}
            onChange={(value: unknown) => {
              const next = String(value || FILTER_ALL);
              applyInboxFilters({
                line_id: next === FILTER_ALL ? null : next,
              });
            }}
            suffixIcon={<DownOutlined />}
            popupMatchSelectWidth={false}
            getPopupContainer={() => document.body}
            dropdownStyle={{ zIndex: 1200 }}
            showSearch
            optionFilterProp="children"
            aria-label={t("inbound.whatsapp.filterLine")}
            $width={220}
          >
            <Option value={FILTER_ALL}>
              {t("inbound.whatsapp.allLines")}
            </Option>
            {lineList.map((line) => {
              const id = String(line?._id || "");
              if (!id) return null;
              const label = lineOptionLabel(line);
              return (
                <Option key={id} value={id} title={label}>
                  {label}
                </Option>
              );
            })}
          </BarSelect>
          <HandledByChecks>
            <Checkbox
              checked={aiOnlyChecked}
              onChange={(event) => {
                applyInboxFilters({
                  handled_by: handledByFromAiOnly(event.target.checked),
                });
              }}
            >
              {t("inbound.whatsapp.handledByAiOnly")}
            </Checkbox>
          </HandledByChecks>
          <BarSelect
            value={assigneeSelectValue}
            onChange={(value: unknown) => {
              const next = String(value || FILTER_ALL);
              if (next === FILTER_ALL) {
                applyInboxFilters({ agent_id: null, assigned_to: null });
                return;
              }
              if (next.startsWith(ASSIGNEE_USER_PREFIX)) {
                applyInboxFilters({
                  agent_id: null,
                  assigned_to: next.slice(ASSIGNEE_USER_PREFIX.length) || null,
                  handled_by: HANDLED_BY_FILTER.ALL,
                });
                return;
              }
              const agentValue = next.startsWith(ASSIGNEE_AGENT_PREFIX)
                ? next.slice(ASSIGNEE_AGENT_PREFIX.length)
                : next;
              applyInboxFilters({
                agent_id: agentValue || null,
                assigned_to: null,
              });
            }}
            suffixIcon={<DownOutlined />}
            popupMatchSelectWidth={false}
            getPopupContainer={() => document.body}
            dropdownStyle={{ zIndex: 1200 }}
            showSearch
            optionFilterProp="children"
            aria-label={t("inbound.whatsapp.filterAgent")}
            $width={260}
          >
            <Option value={FILTER_ALL}>
              {t("inbound.whatsapp.allInboundAgents")}
            </Option>
            {!aiOnlyChecked && teamMembers.length > 0 && (
              <OptGroup label={t("inbound.whatsapp.assignedToTeam")}>
                {teamMembers.map((user) => {
                  const id = String(user?._id || "");
                  if (!id) return null;
                  const label = teamMemberLabel(user);
                  return (
                    <Option
                      key={`${ASSIGNEE_USER_PREFIX}${id}`}
                      value={`${ASSIGNEE_USER_PREFIX}${id}`}
                      title={label}
                    >
                      {label}
                    </Option>
                  );
                })}
              </OptGroup>
            )}
            {inboundAgents.length > 0 && (
              <OptGroup label={t("inbound.whatsapp.assignedToAiAgents")}>
                {inboundAgents.map((agent) => {
                  const id = String(agent?._id || "");
                  if (!id) return null;
                  const label = String(agent.agent_name || "").trim() || id;
                  return (
                    <Option
                      key={`${ASSIGNEE_AGENT_PREFIX}${id}`}
                      value={`${ASSIGNEE_AGENT_PREFIX}${id}`}
                      title={label}
                    >
                      {label}
                    </Option>
                  );
                })}
              </OptGroup>
            )}
          </BarSelect>
          <SearchWrapper>
            <SearchIcon />
            <CustomSearch
              placeholder={t("inbound.whatsapp.searchPhoneOrName")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              allowClear
            />
          </SearchWrapper>
        </FilterBarTools>
      </FilterBar>

      <Split>
        <ListPanel $hidden={isMobile && mobileThreadOpen}>
          <ListHead>
            <span>{t("inbound.whatsapp.customers")}</span>
            <StatusSelect
              value={status}
              onChange={(value: unknown) => {
                if (value == null || value === "") return;
                applyStatusFilter(String(value));
              }}
              suffixIcon={busy ? <LoadingOutlined /> : <DownOutlined />}
              popupMatchSelectWidth
              getPopupContainer={() => document.body}
              dropdownStyle={{ zIndex: 1200 }}
              aria-label={t("inbound.whatsapp.status")}
            >
              <Option value={TICKET_DISPLAY_STATUS.ALL}>
                {t("inbound.whatsapp.filterByStatus")}
              </Option>
              <Option value={TICKET_DISPLAY_STATUS.OPEN}>
                {t("inbound.whatsapp.statusOpen")}
              </Option>
              <Option value={TICKET_DISPLAY_STATUS.NEED_ASSIGN}>
                {t("inbound.whatsapp.needsAssign")}
              </Option>
              <Option value={TICKET_DISPLAY_STATUS.FAILED}>
                {t("inbound.whatsapp.statusFailed")}
              </Option>
              <Option value={TICKET_DISPLAY_STATUS.COMPLETED}>
                {t("inbound.whatsapp.statusCompleted")}
              </Option>
            </StatusSelect>
          </ListHead>
          <ListBody ref={listBodyRef}>
            <ContentLoading spinning={busy} minHeight={280} fill>
            {pagedRows.length === 0 && !loadingMore && !canLoadMore ? (
              showMessagingPrompt ? (
                <MessagingSetupPrompt compact />
              ) : (
                <EmptyCopy>{t("inbound.whatsapp.noMatchFilters")}</EmptyCopy>
              )
            ) : (
              <>
              {pagedRows.map((row) => {
                const assignee = getAssigneeDisplay(
                  row,
                  agentsById,
                  usersById,
                  {
                    needToAssign: t("inbound.whatsapp.needToAssign"),
                    named: (name: string) =>
                      t("inbound.whatsapp.assigneeNamed", { name }),
                  }
                );
                const selected = rowKey(row) === selectedKey;
                const campaignName = String(row.campaignName || "").trim();
                const ticketId = String(row.ticketId || row.id || "").trim();
                const assignable = canAssignTicket(row);
                const assigning = assigningTicketId === ticketId;
                const assignMenuItems: MenuProps["items"] = teamMembers.length
                  ? teamMembers.map((member) => {
                      const memberId = String(member._id || "").trim();
                      return {
                        key: memberId,
                        label: teamMemberLabel(member),
                        disabled: !memberId || assigning,
                      };
                    })
                  : [
                      {
                        key: "__empty",
                        label: t("inbound.whatsapp.noTeamMembers"),
                        disabled: true,
                      },
                    ];
                const assigneeTag = (
                  <Tag
                    $bg={assignee.bg}
                    $fg={assignee.fg}
                    $clickable={assignable}
                    title={
                      assignable ? t("inbound.whatsapp.assignTo") : undefined
                    }
                    onClick={(event) => {
                      if (!assignable) return;
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onMouseDown={(event) => {
                      if (!assignable) return;
                      event.stopPropagation();
                    }}
                  >
                    {assigning ? <LoadingOutlined /> : null}
                    {assignee.label}
                  </Tag>
                );
                return (
                  <CustomerRow
                    key={rowKey(row)}
                    type="button"
                    $selected={selected}
                    onClick={() => selectRow(row)}
                  >
                    <Avatar>{getInitials(row.name, row.phone)}</Avatar>
                    <CustomerCopy>
                      <RowTop>
                        <Identity>
                          <Phone title={row.phone || undefined}>
                            {row.phone || "—"}
                          </Phone>
                          {campaignName ? (
                            <CampaignChipWrap>
                              <Tooltip title={campaignName} placement="top">
                                <CampaignChip>{campaignName}</CampaignChip>
                              </Tooltip>
                            </CampaignChipWrap>
                          ) : null}
                        </Identity>
                        <When>{row.when}</When>
                      </RowTop>
                      <Name>{row.name || "—"}</Name>
                      <SnippetRow>
                        {channelIcon((row.channels && row.channels[0]) || row.channel)}
                        <Snippet title={row.snippet || undefined}>{row.snippet || "—"}</Snippet>
                      </SnippetRow>
                      <TagRow>
                        {assignable ? (
                          <Dropdown
                            trigger={["click"]}
                            disabled={assigning}
                            menu={{
                              items: assignMenuItems,
                              onClick: ({ key, domEvent }) => {
                                domEvent.preventDefault();
                                domEvent.stopPropagation();
                                if (key === "__empty") return;
                                void handleAssignTicket(row, String(key));
                              },
                            }}
                          >
                            {assigneeTag}
                          </Dropdown>
                        ) : (
                          assigneeTag
                        )}
                      </TagRow>
                    </CustomerCopy>
                  </CustomerRow>
                );
              })}
              {canLoadMore ? (
                <LoadMoreSentinel ref={loadMoreSentinelRef} aria-hidden="true" />
              ) : null}
              {loadingMore ? (
                <LoadMoreStatus role="status" aria-live="polite">
                  <LoadingOutlined />
                  {t("inbound.whatsapp.loadingMore")}
                </LoadMoreStatus>
              ) : null}
              </>
            )}
            </ContentLoading>
          </ListBody>
        </ListPanel>

        <ThreadPanel $visible={!isMobile || mobileThreadOpen}>
          {isMobile && mobileThreadOpen ? (
            <MobileThreadBar>
              <MobileBackButton type="button" onClick={closeMobileThread}>
                <LeftOutlined />
                {t("inbound.whatsapp.backToCustomers")}
              </MobileBackButton>
            </MobileThreadBar>
          ) : null}
          <ThreadFill>
            {/* Keyed on the selection so recovering is as simple as picking
                another conversation: one malformed thread can't blank the inbox. */}
            <ErrorBoundary key={selectedKey ?? "empty"}>
              {selectedRow?.channel === "whatsapp" ||
              selectedRow?.channel === "sms" ||
              (!selectedRow && id && !isCampaignView) ? (
                <WhatsappDetail
                  key={selectedRow?.id || id}
                  embedded
                  conversationId={selectedRow?.id || id || ""}
                  campaignId={campaignId || undefined}
                  onConversationUpdated={applyConversationPatch}
                />
              ) : selectedRow ? (
                renderOtherThread(selectedRow)
              ) : showMessagingPrompt ? (
                <EmptyThread>
                  <MessagingSetupPrompt />
                </EmptyThread>
              ) : (
                <EmptyThread>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t("inbound.whatsapp.selectConversation")}
                  />
                </EmptyThread>
              )}
            </ErrorBoundary>
          </ThreadFill>
        </ThreadPanel>
      </Split>
    </Page>
  );
};

const Page = styled.div`
  box-sizing: border-box;
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  overflow: hidden;
  background: ${theme.colors.grey3};
  padding: 32px 32px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (min-width: 1440px) {
    padding: 48px 56px 28px;
    gap: 24px;
  }

  @media (max-width: 1100px) {
    padding: 20px 16px 16px;
    gap: 16px;
  }

  @media (max-width: 900px) {
    height: calc(100dvh - 56px);
    max-height: calc(100dvh - 56px);
    padding: 12px;
    gap: 12px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  gap: 12px 16px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px 14px;
  flex-wrap: wrap;
  min-width: 0;
  flex: 1 1 240px;
`;

const Title = styled.h1`
  margin: 0;
  font-weight: 600;
  font-size: 24px;
  line-height: 1;
  color: #000;

  @media (max-width: 640px) {
    font-size: 20px;
  }
`;

const HeaderMeta = styled.span`
  font-size: 16px;
  line-height: 1.3;
  color: ${theme.colors.grey1};

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const NeedsReplyLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: ${theme.colors.blue1};
  cursor: pointer;
  text-decoration: underline;
  font-size: inherit;
`;

const HeaderFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
  flex-wrap: wrap;
  justify-content: flex-end;
  min-width: 0;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const FilterSelect = styled(Select)<{ $width?: number; $stretch?: boolean }>`
  && {
    width: ${({ $width }) => $width || 180}px;
    height: 42px;
    flex: 0 0 auto;
  }

  .ant-select-selector {
    height: 42px !important;
    min-height: 42px !important;
    border-radius: 8px !important;
    border-color: ${theme.colors.grey2} !important;
    display: flex !important;
    align-items: center !important;
    padding: 0 14px !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    display: flex !important;
    align-items: center !important;
    line-height: 1 !important;
    font-size: 16px;
  }

  @media (max-width: 640px) {
    && {
      width: 100%;
      flex: 1 1 calc(50% - 4px);
    }
  }
`;

const FilterBar = styled.div`
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.09);
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  min-width: 0;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
  }
`;

const FilterBarTools = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
  min-width: 0;
  flex: 1 1 360px;

  @media (max-width: 1100px) {
    flex-wrap: wrap;
  }

  @media (max-width: 900px) {
    margin-left: 0;
    width: 100%;
    flex: 1 1 auto;
  }

  @media (max-width: 640px) {
    flex-wrap: wrap;
  }
`;

const HandledByChecks = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
  white-space: nowrap;

  .ant-checkbox-wrapper {
    font-size: 14px;
    color: ${theme.colors.grey1};
  }

  @media (max-width: 640px) {
    width: 100%;
    flex-wrap: wrap;
  }
`;

const BarSelect = styled(FilterSelect)`
  && {
    flex: 0 0 auto;
  }

  @media (max-width: 900px) {
    && {
      width: ${({ $stretch, $width }) =>
        $stretch ? "100%" : `${$width || 180}px`};
      flex: ${({ $stretch }) => ($stretch ? "1 1 auto" : "1 1 160px")};
    }
  }
`;

const ChannelOption = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;

  .anticon {
    flex: none;
    font-size: 14px;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ChannelCount = styled.em`
  margin-left: auto;
  padding-left: 8px;
  font-style: normal;
  font-size: 13px;
  font-weight: 500;
  color: ${theme.colors.grey1};
  flex: none;
`;

const SearchWrapper = styled.div`
  width: 280px;
  max-width: 100%;
  flex: 1 1 240px;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  box-sizing: border-box;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0 14px;
  background: #fff;
  min-width: 0;

  @media (max-width: 900px) {
    width: auto;
    flex: 1 1 220px;
  }
`;

const CustomSearch = styled(Input)`
  height: 40px;
  border: none;
  box-shadow: none;
  background: transparent;
  padding: 0;

  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
  }
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 380px) minmax(0, 1fr);
  gap: 20px;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  min-width: 0;

  @media (max-width: 1200px) {
    grid-template-columns: minmax(240px, 320px) minmax(0, 1fr);
    gap: 16px;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const ListPanel = styled.div<{ $hidden: boolean }>`
  min-width: 0;
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.09);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    display: ${({ $hidden }) => ($hidden ? "none" : "flex")};
  }
`;

const ListHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 20px;
  border-bottom: 1px solid ${theme.colors.grey2};
  flex-shrink: 0;
  font-weight: 600;
  font-size: 16px;
  color: #000;
  min-width: 0;
`;

const StatusSelect = styled(Select)`
  && {
    width: 160px;
    min-width: 0;
    flex: 0 1 160px;
    height: 36px;
    font-weight: 400;
  }

  .ant-select-selector {
    height: 36px !important;
    min-height: 36px !important;
    border-radius: 8px !important;
    border-color: ${theme.colors.grey2} !important;
    display: flex !important;
    align-items: center !important;
    padding: 0 12px !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    display: flex !important;
    align-items: center !important;
    line-height: 1 !important;
    font-size: 14px;
  }
`;

const ListBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const LoadMoreSentinel = styled.div`
  width: 100%;
  height: 1px;
  flex: none;
`;

const LoadMoreStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 16px 18px;
  font-size: 13px;
  line-height: 1.2;
  color: ${theme.colors.grey1};
`;

const CustomerRow = styled.button<{ $selected: boolean }>`
  width: 100%;
  text-align: left;
  padding: 14px 20px;
  border: none;
  border-bottom: 1px solid ${theme.colors.grey2};
  display: flex;
  gap: 12px;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? theme.colors.grey3 : "#fff")};
  border-left: 3px solid
    ${({ $selected }) => ($selected ? theme.colors.green1 : "transparent")};
  ${filterControlCss}
`;

const Avatar = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${theme.colors.green1};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex: none;
`;

const CustomerCopy = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const RowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const Phone = styled.span`
  font-weight: 600;
  font-size: 16px;
  line-height: 1.2;
  color: #000;
  min-width: 0;
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CampaignChipWrap = styled.span`
  display: flex;
  min-width: 0;
  max-width: 148px;
  flex: 1 1 72px;
`;

const CampaignChip = styled.span`
  display: block;
  max-width: 100%;
  min-width: 0;
  padding: 2px 8px;
  border-radius: 6px;
  background: ${theme.colors.grey5};
  color: #4b5563;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const When = styled.span`
  font-size: 14px;
  line-height: 1;
  color: ${theme.colors.grey1};
  flex: none;
`;

const Name = styled.span`
  font-size: 14px;
  line-height: 1.2;
  color: ${theme.colors.grey1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SnippetRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgb(31, 31, 31);
`;

const Snippet = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 1.2;
  color: #000;
`;

const TagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
`;

const Tag = styled.span<{
  $bg: string;
  $fg: string;
  $clickable?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.2;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  flex: none;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  ${({ $clickable, $fg }) =>
    $clickable
      ? `
    &:hover {
      filter: brightness(0.97);
      box-shadow: inset 0 0 0 1px ${$fg}33;
    }
  `
      : ""}
`;

const Muted = styled.span`
  font-size: 14px;
  line-height: 1.2;
  font-weight: 400;
  color: ${theme.colors.grey1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ThreadPanel = styled.div<{ $visible: boolean }>`
  min-width: 0;
  min-height: 0;
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.09);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    display: ${({ $visible }) => ($visible ? "flex" : "none")};
  }
`;

const MobileThreadBar = styled.div`
  display: none;
  flex-shrink: 0;
  padding: 10px 12px;
  border-bottom: 1px solid ${theme.colors.grey2};

  @media (max-width: 900px) {
    display: flex;
  }
`;

const MobileBackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: none;
  padding: 4px 0;
  color: ${theme.colors.blue1};
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
`;

const ThreadFill = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  & > * {
    flex: 1;
    min-height: 0;
    min-width: 0;
  }
`;

const EmptyThread = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const EmptyCopy = styled.div`
  padding: 22px 20px;
  font-size: 16px;
  line-height: 1.4;
  color: ${theme.colors.grey1};
`;

const MessagingSetupPrompt: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <SetupPrompt $compact={compact}>
      {compact ? null : (
        <SetupTitle>{t("inbound.whatsapp.enableMessagingTitle")}</SetupTitle>
      )}
      <SetupBody>
        {t(
          compact
            ? "inbound.whatsapp.enableMessagingShort"
            : "inbound.whatsapp.enableMessagingBody"
        )}
      </SetupBody>
      <SetupButton type="button" onClick={() => navigate("/settings")}>
        {t("inbound.whatsapp.enableMessagingAction")}
      </SetupButton>
    </SetupPrompt>
  );
};

const SetupPrompt = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $compact }) => ($compact ? "flex-start" : "center")};
  text-align: ${({ $compact }) => ($compact ? "left" : "center")};
  gap: 12px;
  max-width: ${({ $compact }) => ($compact ? "none" : "440px")};
  padding: ${({ $compact }) => ($compact ? "22px 20px" : "8px 16px")};
`;

const SetupTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: #000;
`;

const SetupBody = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #4b5563;
`;

const SetupButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: ${theme.colors.green1};
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;

  &:hover {
    filter: brightness(0.96);
  }
`;

const OtherThread = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const OtherHead = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${theme.colors.grey2};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 16px;
  min-width: 0;
  flex-wrap: wrap;
`;

const OtherIdentity = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
  flex: 1;

  strong,
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    flex: none;
    max-width: 70%;
  }
`;

const SummaryBox = styled.div`
  margin: 16px 20px 0;
  padding: 12px 14px;
  background: ${theme.colors.grey3};
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.4;
  color: #000;
`;

const OtherBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${theme.colors.grey3};
`;

const Bubble = styled.div<{ $out: boolean }>`
  align-self: ${({ $out }) => ($out ? "flex-end" : "flex-start")};
  max-width: min(74%, 640px);
  background: ${({ $out }) => ($out ? theme.colors.green2 : "#fff")};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 16px;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-wrap: anywhere;

  @media (max-width: 900px) {
    max-width: 92%;
  }
`;

export default Whatsapp;
